/**
 * outlive-cart-drawer.js — Cart drawer leve para Shopify Horizon
 *
 * Recursos:
 * - Drawer leve independente do cart-drawer nativo do Horizon
 * - Compatível com botão do header #hz-cart-btn
 * - Compatível com widget de assinatura que chama #hz-cart-drawer.open()
 * - Sincroniza brindes da assinatura
 * - Bloqueia remoção/alteração dos brindes
 * - Mostra desconto aplicado no carrinho
 * - Mostra descontos por item quando disponíveis no /cart.js
 * - Mostra upsells por produto usando dados vindos do snippet Liquid
 * - NOVO: converte item avulso em assinatura direto no drawer (toggle) e
 *   permite escolher a frequência (selling plan) sem sair do carrinho
 */

(function () {
  'use strict';

  if (window.__OutliveCartDrawerLoaded) return;
  window.__OutliveCartDrawerLoaded = true;

  const CONFIG = {
    drawerSelector: '#hz-cart-drawer',
    checkoutUrl: '/checkout',

    standardGiftVariantIds: [
      41668987682910,
      41668986863710,
    ],

    extraGiftVariantId: 42283245174878,

    subscriptionPrices: [
      160.87, 178.74, 185.94, 191.94,
      197.94, 255.91, 255.92, 280.42,
      253.22, 263.42, 271.92, 280.42,
      268.11, 278.91, 287.91, 296.91,
      297.9, 309.9, 319.9, 329.9,
      297.8, 309.8, 319.8, 329.8,
      249.9, 314.9
    ],

    extraGiftPrices: [
      297.8,
      309.8,
      319.8,
      329.8,
    ],

    messages: {
      standard: 'Ao incluir a assinatura, você ganhou 2 brindes: Scoop e Mixer!',
      extra: 'Ao incluir a assinatura, você ganhou 3 brindes: Scoop, Mixer e mais 1 brinde exclusivo!',
      combo: 'Ao adicionar o combo, você ganhou 2 brindes + Frete grátis!',
    },

    /* ===== Toggle de assinatura no drawer ===== */
    subscriptionUI: {
      enabled: true,
      /* Texto do toggle quando o item está avulso. {pct} = % de economia */
      offLabel: 'Assinar e economizar {pct}%',
      offLabelNoPct: 'Assinar e economizar',
      /* Texto do toggle quando o item já é assinatura */
      onLabel: 'Assinatura ativa',
      /* Rótulo do seletor de frequência */
      freqLabel: 'Entregar',
      /* Prefixo do badge que aparece perto do título */
      badgePrefix: 'ASSINATURA',
      /* Cor de destaque (verde Outlive) */
      accent: '#63BB47',
      /* Renomear frequências que vêm do Appstle. Chave = texto original (ou
         parte dele, minúsculo); valor = texto exibido. Deixe vazio para usar
         o nome original do selling plan. */
      planLabelMap: {
        // 'delivery every 1 month': 'Mensalmente',
        // 'delivery every 2 weeks': 'Quinzenalmente',
      },
    },
  };

  const nativeFetch = window.fetch.bind(window);
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function money(cents) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format((Number(cents) || 0) / 100);
  }

  function centsToBRL(cents) {
    return Math.round(Number(cents) || 0) / 100;
  }

  function closeEnough(a, b) {
    return Math.abs(Number(a) - Number(b)) < 0.02;
  }

  function isGiftVariant(id) {
    const n = Number(id);
    return CONFIG.standardGiftVariantIds.includes(n) || n === Number(CONFIG.extraGiftVariantId);
  }

  /* Brinde do widget de combo: identificado pela line item property "_combo_brinde".
     Cada brinde carrega também "_combo_parent" = id do PRODUTO do combo que o liberou. */
  function isComboGift(item) {
    const props = item && item.properties;
    return !!(props && (props._combo_brinde || props['_combo_brinde']));
  }

  function comboGiftParentId(item) {
    const props = (item && item.properties) || {};
    return Number(props._combo_parent || props['_combo_parent']) || null;
  }

  /* Qualquer brinde (assinatura por variante OU combo por property) */
  function isAnyGift(item) {
    return isGiftVariant(item && item.variant_id) || isComboGift(item);
  }

  function isSubPrice(brl) {
    return CONFIG.subscriptionPrices.some((p) => closeEnough(p, brl));
  }

  function isExtraPrice(brl) {
    return CONFIG.extraGiftPrices.some((p) => closeEnough(p, brl));
  }

  function isSubscriptionItem(item) {
    if (!item || isAnyGift(item)) return false;
    return isSubPrice(centsToBRL(item.price));
  }

  function hasExtraGiftTrigger(cart) {
    return (cart.items || []).some((item) => {
      if (!isSubscriptionItem(item)) return false;
      return isExtraPrice(centsToBRL(item.price));
    });
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function getUpsellData() {
    const script = document.getElementById('outlive-cart-upsell-data');
    if (!script) return {};

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[OutliveCartDrawer] Erro ao ler upsells:', error);
      return {};
    }
  }

  /* ============================================================
     SELLING PLANS (assinaturas disponíveis por variante)
     Dados vindos do snippet outlive-cart-selling-plans.liquid
     ============================================================ */

  let sellingPlansCache = null;

  function getSellingPlansData() {
    if (sellingPlansCache) return sellingPlansCache;

    const script = document.getElementById('outlive-selling-plans-data');

    if (!script) {
      sellingPlansCache = {};
      return sellingPlansCache;
    }

    try {
      sellingPlansCache = JSON.parse(script.textContent || '{}') || {};
    } catch (error) {
      console.warn('[OutliveCartDrawer] Erro ao ler selling plans:', error);
      sellingPlansCache = {};
    }

    return sellingPlansCache;
  }

  /* Fallback: se a variante não estiver no JSON (produto fora das coleções
     listadas no snippet), tenta buscar via /products/{handle}.js e guarda
     no cache. Silencioso se o endpoint não trouxer selling plans. */
  const fetchedHandles = new Set();

  async function tryFetchPlansForHandle(handle) {
    if (!handle || fetchedHandles.has(handle)) return false;
    fetchedHandles.add(handle);

    try {
      const res = await nativeFetch(`/products/${handle}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!res.ok) return false;

      const product = await res.json();
      const groups = product.selling_plan_groups || [];

      if (!groups.length) return false;

      const plans = [];

      groups.forEach((group) => {
        (group.selling_plans || []).forEach((plan) => {
          if (plan && plan.id) {
            plans.push({
              id: plan.id,
              name: plan.name || 'Assinatura',
              price: null,
              group: group.id || null,
            });
          }
        });
      });

      if (!plans.length) return false;

      const data = getSellingPlansData();

      (product.variants || []).forEach((variant) => {
        if (!variant || !variant.id) return;
        if (data[String(variant.id)]) return;

        /* Preço por plano, quando o endpoint expõe as allocations */
        const allocations = variant.selling_plan_allocations || [];

        const variantPlans = plans.map((plan) => {
          const allocation = allocations.find((a) => {
            return Number(a.selling_plan_id) === Number(plan.id);
          });

          return {
            ...plan,
            price: allocation ? allocation.price : null,
          };
        });

        data[String(variant.id)] = {
          handle: product.handle,
          base_price: variant.price,
          plans: variantPlans,
        };
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  function plansForVariant(variantId) {
    const entry = getSellingPlansData()[String(variantId)];
    if (!entry || !Array.isArray(entry.plans) || !entry.plans.length) return null;
    return entry;
  }

  function planLabel(name) {
    const map = CONFIG.subscriptionUI.planLabelMap || {};
    const raw = String(name || '').trim();
    const lower = raw.toLowerCase();

    if (map[raw]) return map[raw];
    if (map[lower]) return map[lower];

    const hit = Object.keys(map).find((key) => lower.includes(String(key).toLowerCase()));

    return hit ? map[hit] : raw;
  }

  function currentPlanId(item) {
    return item?.selling_plan_allocation?.selling_plan?.id
      ? Number(item.selling_plan_allocation.selling_plan.id)
      : null;
  }

  /* Plano com o MAIOR desconto (menor preço) — usado no rótulo e ao ativar o toggle */
  function bestPlan(entry) {
    const plans = (entry?.plans || []).filter((plan) => plan && plan.id);

    if (!plans.length) return null;

    const priced = plans.filter((plan) => Number(plan.price) > 0);

    /* Sem preços conhecidos (ex: fallback via product.js): usa o primeiro */
    if (!priced.length) return plans[0];

    return priced.reduce((best, plan) => {
      return Number(plan.price) < Number(best.price) ? plan : best;
    }, priced[0]);
  }

  /* Melhor desconto disponível (usado no rótulo "economizar X%") */
  function bestSavingsPct(entry, fallbackBasePrice) {
    const base = Number(entry?.base_price) || Number(fallbackBasePrice) || 0;
    if (!base) return 0;

    const plan = bestPlan(entry);
    const price = Number(plan?.price);

    if (!price || price >= base) return 0;

    return Math.round(((base - price) / base) * 100);
  }

  async function fetchCart() {
    const res = await nativeFetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    });

    if (!res.ok) throw new Error('Erro ao carregar carrinho.');
    return res.json();
  }

  async function addItems(items) {
    const res = await nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items }),
    });

    if (!res.ok) throw new Error('Erro ao adicionar item.');
    return res.json();
  }

  async function updateItems(updates) {
    const res = await nativeFetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });

    if (!res.ok) throw new Error('Erro ao atualizar carrinho.');
    return res.json();
  }

  async function changeLine(line, quantity) {
    const res = await nativeFetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        line,
        quantity,
      }),
    });

    if (!res.ok) throw new Error('Erro ao alterar item.');
    return res.json();
  }

  /* ============================================================
     CONVERSÃO AVULSO <-> ASSINATURA
     Estratégia:
     1. Tenta /cart/change.js passando "selling_plan" (mantém a posição do item)
     2. Se o carrinho não refletir a mudança, faz fallback: remove a linha e
        adiciona novamente com (ou sem) selling_plan, preservando qtd/properties
     ============================================================ */

  function cartHasVariantWithPlan(cart, variantId, planId) {
    return (cart.items || []).some((item) => {
      if (Number(item.variant_id) !== Number(variantId)) return false;

      const itemPlan = currentPlanId(item);

      return planId ? Number(itemPlan) === Number(planId) : !itemPlan;
    });
  }

  async function setLineSellingPlan(item, planId) {
    const quantity = Number(item.quantity) || 1;
    const variantId = Number(item.variant_id);

    /* 1) change.js com selling_plan */
    let cart = null;

    try {
      const payload = {
        id: item.key,
        quantity,
        selling_plan: planId ? Number(planId) : null,
      };

      const res = await nativeFetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      if (res.ok) cart = await res.json();
    } catch (error) {
      cart = null;
    }

    if (cart && cartHasVariantWithPlan(cart, variantId, planId)) {
      return cart;
    }

    /* 2) Fallback: remove + adiciona novamente */
    const properties = {};

    Object.entries(item.properties || {}).forEach(([key, value]) => {
      if (value) properties[key] = value;
    });

    await updateItems({ [item.key]: 0 });

    const addPayload = {
      id: variantId,
      quantity,
    };

    if (planId) addPayload.selling_plan = Number(planId);
    if (Object.keys(properties).length) addPayload.properties = properties;

    await addItems([addPayload]);

    return fetchCart();
  }

  function getCartDiscountLabels(cart) {
    const labels = [];

    (cart.cart_level_discount_applications || []).forEach((discount) => {
      if (discount?.title) labels.push(discount.title);
    });

    (cart.discount_applications || []).forEach((discount) => {
      if (discount?.title) labels.push(discount.title);
    });

    return [...new Set(labels.filter(Boolean))];
  }

  function getItemDiscountLabels(item) {
    const labels = [];

    (item.line_level_discount_allocations || []).forEach((allocation) => {
      if (allocation?.discount_application?.title) {
        labels.push(allocation.discount_application.title);
      }
    });

    (item.discounts || []).forEach((discount) => {
      if (discount?.title) labels.push(discount.title);
    });

    return [...new Set(labels.filter(Boolean))];
  }

  function syncBubble(count) {
    const itemCount = Number(count) || 0;

    document.querySelectorAll('[ref="cartBubbleCount"]').forEach((el) => {
      el.textContent = itemCount > 0 ? String(itemCount) : '';
      el.classList.toggle('hidden', itemCount === 0);
    });

    document.querySelectorAll('[ref="cartBubble"]').forEach((el) => {
      el.classList.toggle('visually-hidden', itemCount === 0);

      if (itemCount <= 99) {
        el.setAttribute('data-maintain-ratio', '');
      } else {
        el.removeAttribute('data-maintain-ratio');
      }
    });

    document.querySelectorAll('.header-actions__cart-icon').forEach((el) => {
      el.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });
  }

  function expectedGiftsMap(cartItems) {
    let subscriptionQty = 0;
    let hasExtra = false;

    (cartItems || []).forEach((item) => {
      if (!isSubscriptionItem(item)) return;

      subscriptionQty += Number(item.quantity) || 0;

      if (isExtraPrice(centsToBRL(item.price))) {
        hasExtra = true;
      }
    });

    const expected = new Map();

    if (subscriptionQty <= 0) return expected;

    CONFIG.standardGiftVariantIds.forEach((id) => {
      expected.set(Number(id), subscriptionQty);
    });

    if (hasExtra) {
      expected.set(Number(CONFIG.extraGiftVariantId), subscriptionQty);
    }

    return expected;
  }

  let syncingGifts = false;
  let pendingSync = null;

  async function syncGifts(cartData = null) {
    if (syncingGifts) return cartData || fetchCart();

    syncingGifts = true;

    try {
      let cart = cartData || await fetchCart();

      const expected = expectedGiftsMap(cart.items);
      const current = new Map();

      (cart.items || []).forEach((item) => {
        /* IMPORTANTE: brindes de combo (property _combo_brinde) NÃO entram aqui.
           Eles compartilham as mesmas variantes (scoop/mixer) dos brindes de
           assinatura, mas são gerenciados pela rotina de brindes de combo abaixo.
           Sem isso, a lógica de assinatura os removeria como "órfãos". */
        if (isComboGift(item)) return;
        if (isGiftVariant(item.variant_id)) {
          current.set(Number(item.variant_id), Number(item.quantity) || 0);
        }
      });

      const updates = {};
      const toAdd = [];

      expected.forEach((qty, variantId) => {
        if (!current.has(variantId)) {
          toAdd.push({
            id: variantId,
            quantity: qty,
          });
        } else if (current.get(variantId) !== qty) {
          updates[variantId] = qty;
        }
      });

      current.forEach((qty, variantId) => {
        if (!expected.has(variantId)) {
          updates[variantId] = 0;
        }
      });

      if (Object.keys(updates).length) {
        await updateItems(updates);
        cart = await fetchCart();
      }

      if (toAdd.length) {
        await addItems(toAdd);
        cart = await fetchCart();
      }

      /* ============================================================
         BRINDES DO COMBO (via line item property)
         - Cada brinde carrega _combo_parent = ID do PRODUTO do combo.
         - O brinde é removido quando esse produto não está mais no carrinho.
         - Os brindes ficam travados no drawer (não removíveis manualmente),
           então só saem junto com o combo.
         ============================================================ */
      const comboItems = cart.items || [];
      const nonGiftProductIds = new Set();
      comboItems.forEach((item) => {
        if (isAnyGift(item)) return;
        if (item.product_id != null) nonGiftProductIds.add(Number(item.product_id));
      });

      const comboUpdates = {};
      comboItems.forEach((item) => {
        if (!isComboGift(item)) return;
        const parentProductId = comboGiftParentId(item);
        if (!parentProductId || !nonGiftProductIds.has(parentProductId)) {
          comboUpdates[item.key] = 0;
        }
      });

      if (Object.keys(comboUpdates).length) {
        await updateItems(comboUpdates);
        cart = await fetchCart();
      }

      syncBubble(cart.item_count);

      return cart;
    } finally {
      syncingGifts = false;
    }
  }

  function runGiftSync(cartData = null) {
    const promise = syncGifts(cartData).catch(() => cartData || null);

    pendingSync = promise.finally(() => {
      if (pendingSync === promise) pendingSync = null;
    });

    return promise;
  }

  async function waitForGiftSync() {
    const startedAt = Date.now();

    if (pendingSync) {
      await Promise.race([
        pendingSync,
        delay(2000),
      ]);
    }

    while (syncingGifts && Date.now() - startedAt < 2000) {
      await delay(50);
    }
  }

  /* ============================================================
     ESTILOS DO TOGGLE DE ASSINATURA
     (pode ser movido para o CSS do tema se preferir)
     ============================================================ */
  function injectSubscriptionStyles() {
    if (!CONFIG.subscriptionUI.enabled) return;
    if (document.getElementById('ocd-sub-styles')) return;

    const accent = CONFIG.subscriptionUI.accent;
    const style = document.createElement('style');
    style.id = 'ocd-sub-styles';
    style.textContent = `
      .ocd-sub { display: grid; gap: 8px; margin-top: 10px; }
      .ocd-sub__toggle {
        appearance: none;
        width: 100%;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 9px 12px;
        border-radius: 10px;
        border: 1.5px solid rgba(0,0,0,0.12);
        background: rgba(0,0,0,0.02);
        cursor: pointer;
        font-family: inherit;
        font-size: 0.82rem;
        font-weight: 600;
        color: rgba(0,0,0,0.7);
        text-align: left;
        transition: border-color 0.18s, background 0.18s, color 0.18s;
      }
      .ocd-sub__toggle:hover { border-color: rgba(0,0,0,0.25); }
      .ocd-sub__toggle.is-active {
        border-color: ${accent};
        background: rgba(99,187,71,0.10);
        color: #2f6b1f;
      }
      .ocd-sub__toggle[disabled] { opacity: 0.6; cursor: wait; }
      .ocd-sub__icon { flex-shrink: 0; font-size: 0.95rem; line-height: 1; }
      .ocd-sub__label { flex: 1; }
      .ocd-sub__switch {
        flex-shrink: 0;
        width: 38px; height: 22px;
        border-radius: 100px;
        background: rgba(0,0,0,0.18);
        position: relative;
        transition: background 0.2s;
      }
      .ocd-sub__toggle.is-active .ocd-sub__switch { background: ${accent}; }
      .ocd-sub__knob {
        position: absolute;
        top: 3px; left: 3px;
        width: 16px; height: 16px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0,0,0,0.25);
        transition: transform 0.2s;
      }
      .ocd-sub__toggle.is-active .ocd-sub__knob { transform: translateX(16px); }
      .ocd-sub__freq {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border-radius: 10px;
        background: rgba(99,187,71,0.08);
      }
      .ocd-sub__freq-label {
        flex-shrink: 0;
        font-size: 0.78rem;
        font-weight: 600;
        color: #2f6b1f;
      }
      .ocd-sub__select {
        flex: 1;
        appearance: none;
        -webkit-appearance: none;
        background: #fff url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23555' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 8px center;
        background-size: 16px;
        border: 1.5px solid rgba(0,0,0,0.14);
        border-radius: 8px;
        padding: 8px 30px 8px 10px;
        font-family: inherit;
        font-size: 0.85rem;
        font-weight: 600;
        color: #222;
        cursor: pointer;
        min-width: 0;
      }
      .ocd-sub__select:focus { outline: none; border-color: ${accent}; }
      .ocd-item__selling-plan {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin: 4px 0 0;
        padding: 2px 8px;
        border-radius: 100px;
        background: rgba(99,187,71,0.12);
        color: #2f6b1f;
        font-size: 0.68rem;
        font-weight: 700;
        letter-spacing: 0.02em;
      }
    `;

    document.head.appendChild(style);
  }

  class OutliveCartDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = this.querySelector('[data-ocd-overlay]');
      this.panel = this.querySelector('[data-ocd-panel]');
      this.content = this.querySelector('[data-ocd-content]');
      this.footer = this.querySelector('[data-ocd-footer]');
      this.empty = this.querySelector('[data-ocd-empty]');
      this.loading = this.querySelector('[data-ocd-loading]');
      this.errorBox = this.querySelector('[data-ocd-error]');
      this.subtotal = this.querySelector('[data-ocd-subtotal]');
      this.count = this.querySelector('[data-ocd-count]');

      /* Guarda o carrinho atual para as ações de assinatura */
      this.cart = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.hasAttribute('open')) {
          this.close();
        }
      });

      fetchCart()
        .then((cart) => {
          syncBubble(cart.item_count);
          this.render(cart);
        })
        .catch(() => {});
    }

    async open() {
      this.setAttribute('open', '');
      document.documentElement.classList.add('outlive-cart-drawer-is-open');
      document.body.classList.add('outlive-cart-drawer-is-open');

      this.setLoading(true);
      this.clearError();

      try {
        let cart = await fetchCart();
        cart = await syncGifts(cart);
        this.render(cart);
      } catch (error) {
        this.showError('Não foi possível carregar o carrinho. Tente novamente.');
      } finally {
        this.setLoading(false);
      }
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('outlive-cart-drawer-is-open');
      document.body.classList.remove('outlive-cart-drawer-is-open');
    }

    setLoading(isLoading) {
      this.toggleAttribute('loading', isLoading);

      if (this.loading) {
        this.loading.hidden = !isLoading;
      }
    }

    showError(message) {
      if (!this.errorBox) return;

      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }

    clearError() {
      if (!this.errorBox) return;

      this.errorBox.textContent = '';
      this.errorBox.hidden = true;
    }

    findItemByKey(key) {
      if (!key) return null;
      return (this.cart?.items || []).find((item) => String(item.key) === String(key)) || null;
    }

    async onClick(event) {
      const closeBtn = event.target.closest('[data-ocd-close]');
      const checkoutBtn = event.target.closest('[data-ocd-checkout]');
      const quantityBtn = event.target.closest('[data-ocd-quantity]');
      const removeBtn = event.target.closest('[data-ocd-remove]');
      const upsellBtn = event.target.closest('[data-ocd-upsell-add]');
      const subToggle = event.target.closest('[data-ocd-sub-toggle]');

      if (closeBtn || event.target === this.overlay) {
        event.preventDefault();
        this.close();
        return;
      }

      if (checkoutBtn) {
        event.preventDefault();

        checkoutBtn.disabled = true;

        try {
          await waitForGiftSync();
          window.location.href = CONFIG.checkoutUrl;
        } finally {
          checkoutBtn.disabled = false;
        }

        return;
      }

      if (subToggle) {
        event.preventDefault();

        const key = subToggle.dataset.key;
        const isActive = subToggle.classList.contains('is-active');
        const planId = isActive ? null : Number(subToggle.dataset.plan) || null;

        if (!isActive && !planId) return;

        subToggle.disabled = true;

        await this.changeSubscription(key, planId);
        return;
      }

      if (upsellBtn) {
        event.preventDefault();

        const variantId = upsellBtn.dataset.variantId;
        if (!variantId) return;

        await this.addUpsell(variantId, upsellBtn);
        return;
      }

      if (quantityBtn) {
        event.preventDefault();

        const line = Number(quantityBtn.dataset.line);
        const qty = Number(quantityBtn.dataset.quantity);

        if (!line || qty < 0) return;

        await this.updateLine(line, qty);
        return;
      }

      if (removeBtn) {
        event.preventDefault();

        const line = Number(removeBtn.dataset.line);

        if (!line) return;

        await this.updateLine(line, 0);
      }
    }

    async onChange(event) {
      const select = event.target.closest('[data-ocd-sub-plan]');
      if (!select) return;

      const key = select.dataset.key;
      const planId = Number(select.value) || null;

      if (!planId) return;

      select.disabled = true;

      await this.changeSubscription(key, planId);
    }

    /* Liga/desliga assinatura ou troca a frequência de uma linha */
    async changeSubscription(key, planId) {
      const item = this.findItemByKey(key);

      if (!item) {
        this.showError('Item não encontrado. Recarregue o carrinho.');
        return;
      }

      this.setLoading(true);
      this.clearError();

      try {
        let cart = await setLineSellingPlan(item, planId);
        cart = await syncGifts(cart);
        this.render(cart);
      } catch (error) {
        this.showError('Não foi possível alterar a assinatura. Tente novamente.');

        try {
          this.render(await fetchCart());
        } catch (_) {}
      } finally {
        this.setLoading(false);
      }
    }

    async updateLine(line, quantity) {
      this.setLoading(true);
      this.clearError();

      try {
        let cart = await changeLine(line, quantity);
        cart = await syncGifts(cart);
        this.render(cart);
      } catch (error) {
        this.showError('Não foi possível atualizar o carrinho. Tente novamente.');
      } finally {
        this.setLoading(false);
      }
    }

    async addUpsell(variantId, button = null) {
      if (!variantId) return;

      const originalText = button ? button.textContent : '';

      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
        button.textContent = '...';
        button.setAttribute('aria-label', 'Adicionando produto');
      }

      this.setLoading(true);
      this.clearError();

      try {
        await addItems([
          {
            id: Number(variantId),
            quantity: 1,
          },
        ]);

        let cart = await fetchCart();
        cart = await syncGifts(cart);

        this.render(cart);
      } catch (error) {
        this.showError('Não foi possível adicionar este produto. Tente novamente.');
      } finally {
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
          button.textContent = originalText || 'Adicionar';
          button.removeAttribute('aria-label');
        }

        this.setLoading(false);
      }
    }

    render(cart) {
      this.cart = cart;

      const items = cart.items || [];
      const normalItems = items.filter((item) => !isAnyGift(item));
      const giftItems = items.filter((item) => isAnyGift(item));
      const orderedItems = [...normalItems, ...giftItems];
      const hasItems = orderedItems.length > 0;
      const hasSub = normalItems.some(isSubscriptionItem);
      const hasExtra = hasExtraGiftTrigger(cart);
      const hasComboGifts = items.some(isComboGift);
      const cartDiscountLabels = getCartDiscountLabels(cart);

      syncBubble(cart.item_count);

      if (this.count) {
        this.count.textContent = cart.item_count === 1
          ? '1 item'
          : `${cart.item_count || 0} itens`;
      }

      if (this.subtotal) {
        this.subtotal.textContent = money(cart.total_price);
      }

      if (this.empty) {
        this.empty.hidden = hasItems;
      }

      if (this.footer) {
        this.footer.hidden = !hasItems;
      }

      const discountTarget = this.querySelector('[data-ocd-discounts]');

      if (discountTarget) {
        if (cartDiscountLabels.length) {
          discountTarget.hidden = false;
          discountTarget.innerHTML = cartDiscountLabels.map((label) => {
            return `
              <div class="ocd-discount-line">
                <span>Desconto aplicado</span>
                <strong>${escapeHtml(label)}</strong>
              </div>
            `;
          }).join('');
        } else {
          discountTarget.hidden = true;
          discountTarget.innerHTML = '';
        }
      }

      if (!this.content) return;

      if (!hasItems) {
        this.content.innerHTML = '';
        return;
      }

      this.content.innerHTML = `
        ${hasSub
          ? this.renderGiftMessage(hasExtra ? 'extra' : 'standard')
          : (hasComboGifts ? this.renderGiftMessage('combo') : '')}
        <div class="ocd-items">
          ${orderedItems.map((item) => this.renderItem(item, items.indexOf(item) + 1)).join('')}
        </div>
        ${this.renderUpsells(normalItems)}
      `;

      /* Busca planos de produtos que não vieram no JSON do Liquid e, se achar,
         re-renderiza uma única vez para exibir o toggle. */
      this.hydrateMissingPlans(normalItems);
    }

    async hydrateMissingPlans(items) {
      if (!CONFIG.subscriptionUI.enabled) return;

      const pending = (items || []).filter((item) => {
        return item.handle && !plansForVariant(item.variant_id) && !fetchedHandles.has(item.handle);
      });

      if (!pending.length) return;

      const results = await Promise.all(
        pending.map((item) => tryFetchPlansForHandle(item.handle))
      );

      if (results.some(Boolean) && this.cart) {
        this.render(this.cart);
      }
    }

    renderGiftMessage(type) {
      const text = CONFIG.messages[type];

      if (!text) return '';

      return `
        <div class="ocd-gift-message" role="status" aria-live="polite">
          <span class="ocd-gift-message__icon">🎁</span>
          <span>${escapeHtml(text)}</span>
        </div>
      `;
    }

    /* Toggle de assinatura + seletor de frequência para uma linha */
    renderSubscription(item) {
      if (!CONFIG.subscriptionUI.enabled) return '';
      if (isAnyGift(item)) return '';

      const entry = plansForVariant(item.variant_id);
      const activePlan = currentPlanId(item);

      /* Sem planos conhecidos: só mostra se já for assinatura (para poder desligar) */
      if (!entry && !activePlan) return '';

      const ui = CONFIG.subscriptionUI;
      const plans = entry ? entry.plans : [];
      const isActive = !!activePlan;

      /* Plano usado ao ligar o toggle: o atual, senão o de MAIOR desconto */
      const defaultPlan = activePlan || bestPlan(entry)?.id || '';

      let label;

      if (isActive) {
        label = ui.onLabel;
      } else {
        const pct = bestSavingsPct(entry, item.original_price || item.price);
        label = pct > 0
          ? ui.offLabel.replace('{pct}', pct)
          : ui.offLabelNoPct;
      }

      const options = plans.map((plan) => {
        const selected = Number(plan.id) === Number(activePlan) ? ' selected' : '';
        return `<option value="${escapeHtml(plan.id)}"${selected}>${escapeHtml(planLabel(plan.name))}</option>`;
      }).join('');

      const freq = (isActive && plans.length > 1)
        ? `
          <div class="ocd-sub__freq">
            <span class="ocd-sub__freq-label">⟳ ${escapeHtml(ui.freqLabel)}</span>
            <select class="ocd-sub__select" data-ocd-sub-plan data-key="${escapeHtml(item.key)}" aria-label="${escapeHtml(ui.freqLabel)}">
              ${options}
            </select>
          </div>
        `
        : '';

      return `
        <div class="ocd-sub">
          <button
            type="button"
            class="ocd-sub__toggle${isActive ? ' is-active' : ''}"
            data-ocd-sub-toggle
            data-key="${escapeHtml(item.key)}"
            data-plan="${escapeHtml(defaultPlan)}"
            role="switch"
            aria-checked="${isActive}"
          >
            <span class="ocd-sub__icon">⟳</span>
            <span class="ocd-sub__label">${escapeHtml(label)}</span>
            <span class="ocd-sub__switch"><span class="ocd-sub__knob"></span></span>
          </button>
          ${freq}
        </div>
      `;
    }

    renderItem(item, line) {
      const isGift = isAnyGift(item);

      const image = item.image
        ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
        : `<div class="ocd-item__placeholder"></div>`;

      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="ocd-item__variant">${escapeHtml(item.variant_title)}</p>`
        : '';

      const planName = item.selling_plan_allocation?.selling_plan?.name;

      const sellingPlan = planName
        ? `<p class="ocd-item__selling-plan">⟳ ${escapeHtml(CONFIG.subscriptionUI.badgePrefix)} · ${escapeHtml(planLabel(planName))}</p>`
        : '';

      const discountLabels = getItemDiscountLabels(item);

      const discountsHtml = discountLabels.length
        ? `
          <div class="ocd-item__discounts">
            ${discountLabels.map((label) => `<span>${escapeHtml(label)}</span>`).join('')}
          </div>
        `
        : '';

      const properties = Object.entries(item.properties || {})
        .filter(([key, value]) => value && !String(key).startsWith('_'))
        .map(([key, value]) => {
          return `<p class="ocd-item__property">${escapeHtml(key)}: ${escapeHtml(value)}</p>`;
        })
        .join('');

      const qtyControls = isGift
        ? `<div class="ocd-item__gift-lock">Brinde grátis</div>`
        : `
          <div class="ocd-qty" aria-label="Quantidade">
            <button
              type="button"
              data-ocd-quantity
              data-line="${line}"
              data-quantity="${Math.max(0, item.quantity - 1)}"
              aria-label="Diminuir quantidade"
            >
              −
            </button>

            <span>${item.quantity}</span>

            <button
              type="button"
              data-ocd-quantity
              data-line="${line}"
              data-quantity="${item.quantity + 1}"
              aria-label="Aumentar quantidade"
            >
              +
            </button>
          </div>

          <button
            type="button"
            class="ocd-item__remove"
            data-ocd-remove
            data-line="${line}"
          >
            Remover
          </button>
        `;

      const originalPrice = item.original_line_price && item.original_line_price > item.final_line_price
        ? `<span class="ocd-item__compare">${money(item.original_line_price)}</span>`
        : '';

      const price = isGift
        ? `<span class="ocd-item__free">Grátis</span>`
        : `${originalPrice}<span>${money(item.final_line_price)}</span>`;

      return `
        <article
          class="ocd-item${isGift ? ' ocd-item--gift' : ''}"
          data-line="${line}"
          data-key="${escapeHtml(item.key)}"
        >
          <a class="ocd-item__media" href="${escapeHtml(item.url || '#')}">
            ${image}
          </a>

          <div class="ocd-item__main">
            ${isGift ? '<span class="ocd-item__badge">🎁 Brinde</span>' : ''}

            <a class="ocd-item__title" href="${escapeHtml(item.url || '#')}">
              ${escapeHtml(item.product_title)}
            </a>

            ${variantTitle}
            ${sellingPlan}
            ${discountsHtml}
            ${properties}

            <div class="ocd-item__bottom">
              <div class="ocd-item__actions">
                ${qtyControls}
              </div>

              <div class="ocd-item__price">
                ${price}
              </div>
            </div>

            ${this.renderSubscription(item)}
          </div>
        </article>
      `;
    }

    renderUpsells(items) {
      const upsellData = getUpsellData();
      const alreadyInCart = new Set((items || []).map((item) => String(item.handle)));
      const upsells = [];
      const seen = new Set();

      (items || []).forEach((item) => {
        const handle = item.handle;
        const source = upsellData[handle];

        if (!source) return;

        const type = item.selling_plan_allocation ? 'assinatura' : 'avulsa';
        const list = source[type] || [];

        list.forEach((product) => {
          if (!product || !product.id) return;
          if (seen.has(String(product.id))) return;
          if (alreadyInCart.has(String(product.handle))) return;

          seen.add(String(product.id));
          upsells.push(product);
        });
      });

      if (!upsells.length) return '';

      return `
        <section class="ocd-upsells" aria-label="Você também pode gostar">
          <div class="ocd-upsells__header">
            <h3>Você também pode gostar</h3>
          </div>

          <div class="ocd-upsells__track">
            ${upsells.map((product) => this.renderUpsellCard(product)).join('')}
          </div>
        </section>
      `;
    }

    renderUpsellCard(product) {
      const image = product.image
        ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
        : `<div class="ocd-upsell__placeholder"></div>`;

      const disabled = !product.available ? 'disabled' : '';
      const buttonText = product.available ? 'Adicionar' : 'Esgotado';

      return `
        <article class="ocd-upsell">
          <a href="${escapeHtml(product.url || '#')}" class="ocd-upsell__image">
            ${image}
          </a>

          <div class="ocd-upsell__info">
            <a href="${escapeHtml(product.url || '#')}" class="ocd-upsell__title">
              ${escapeHtml(product.title)}
            </a>

            <div class="ocd-upsell__bottom">
              <span class="ocd-upsell__price">
                ${money(product.price)}
              </span>

              <button
                type="button"
                class="ocd-upsell__button"
                data-ocd-upsell-add
                data-variant-id="${escapeHtml(product.id)}"
                ${disabled}
              >
                ${buttonText}
              </button>
            </div>
          </div>
        </article>
      `;
    }
  }

  if (!customElements.get('outlive-cart-drawer')) {
    customElements.define('outlive-cart-drawer', OutliveCartDrawer);
  }

  function getDrawer() {
    return document.querySelector(CONFIG.drawerSelector);
  }

  function openDrawer() {
    const drawer = getDrawer();

    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  function interceptCartLinks() {
    document.addEventListener('click', (event) => {
      const headerCartButton = event.target.closest('#hz-cart-btn');
      const cartLink = event.target.closest('a[href="/cart"], a[href*="/cart?"], [data-cart-drawer-open]');

      if (!headerCartButton && !cartLink) return;

      event.preventDefault();
      openDrawer();
    });
  }

  function patchFetchForCart() {
    window.fetch = async function (input, init, ...rest) {
      const url = typeof input === 'string' ? input : (input?.url || '');
      const method = (init?.method || 'GET').toUpperCase();

      const isCartAdd = method === 'POST' && url.includes('/cart/add');
      const isCartChange = method === 'POST' && (
        url.includes('/cart/change') ||
        url.includes('/cart/update')
      );

      const response = await nativeFetch(input, init, ...rest);

      if (isCartAdd || isCartChange) {
        response.clone().json().then(async (data) => {
          const addedItems = data.items || (data.id ? [data] : []);
          const addedNonGift = addedItems.some((item) => !isGiftVariant(item.variant_id));

          let cart = null;

          /**
           * Produto adicionado pelo botão nativo da PDP.
           * Aqui precisamos:
           * 1. buscar o carrinho atualizado
           * 2. sincronizar brindes se necessário
           * 3. atualizar bubble
           * 4. renderizar o drawer
           * 5. abrir o drawer
           */
          if (isCartAdd && addedNonGift) {
            await delay(120);

            cart = await fetchCart();
            cart = await runGiftSync(cart);

            syncBubble(cart.item_count);

            const drawer = getDrawer();

            if (drawer) {
              drawer.render?.(cart);
              drawer.open?.();
            }

            return;
          }

          /**
           * Alteração de quantidade/remover item.
           * Aqui não precisa abrir automaticamente, só atualizar se o drawer estiver aberto.
           */
          if (isCartChange) {
            await delay(80);

            cart = data.items ? data : await fetchCart();

            const hasGiftOrSub = (cart.items || []).some((item) => {
              return isGiftVariant(item.variant_id) || isSubscriptionItem(item);
            });

            cart = hasGiftOrSub ? await runGiftSync(cart) : cart;

            syncBubble(cart.item_count);

            const drawer = getDrawer();

            if (drawer?.hasAttribute('open')) {
              drawer.render(cart);
            }
          }
        }).catch(() => {});
      }

      return response;
    };
  }

  function exposeGlobalApi() {
    window.OutliveCartDrawer = {
      open: openDrawer,

      close: () => {
        getDrawer()?.close?.();
      },

      refresh: async () => {
        const drawer = getDrawer();

        let cart = await fetchCart();
        cart = await syncGifts(cart);

        drawer?.render?.(cart);

        return cart;
      },

      syncGifts,

      /* Utilitários de assinatura (debug/uso externo) */
      sellingPlans: getSellingPlansData,
      setLineSellingPlan,
    };
  }

  function init() {
    injectSubscriptionStyles();
    interceptCartLinks();
    patchFetchForCart();
    exposeGlobalApi();

    fetchCart()
      .then(async (cart) => {
        const hasGiftOrSub = (cart.items || []).some((item) => {
          return isGiftVariant(item.variant_id) || isSubscriptionItem(item) || isComboGift(item);
        });

        const finalCart = hasGiftOrSub ? await syncGifts(cart) : cart;

        syncBubble(finalCart.item_count);
        getDrawer()?.render?.(finalCart);
      })
      .catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
